import { useQuery } from '@apollo/client';
import React, { useContext } from 'react';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { maxMakeable } from '../../graphql/entities/product/Product';
import { default as Form } from '../../components/connectedForm';
import GridContainer from '../../components/connectedForm/GridContainer';
import stockTotalsQuery from '../../graphql/queries/product/StockTotalsQuery.graphql';
import Formatters from '../../components/grid-addons/formatters';
import { IProduct, IStockTotalQueryVariables, IStockTotalsQueryResult } from '../../graphql/schema/p4types';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import EditableDataGrid from '../../components/EditableDataGrid/EditableDataGrid';

const NumericFormatter = Formatters.NumericFormatter;

export const StockLevelTab = ({ productId }: { productId?: number }) => {
  const connectedForm = useContext(ConnectedFormContext);
  const values = connectedForm.values as IProduct;
  const precision = values?.precision || 0;

  const stockTotalsColumns: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Warehouse',
      key: 'warehouse.description',
      editable: false,
      resizable: true,
    },
    {
      name: 'Level',
      key: 'level',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Free Stock',
      key: 'freeStock',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Makeable',
      key: 'makeable',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
        infinityLimit: maxMakeable,
      },
    },
    {
      name: 'Allocated',
      key: 'allocated',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Production Required',
      key: 'productionRequired',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'In Production',
      key: 'inProduction',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Issued',
      key: 'issued',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'On Order',
      key: 'onOrder',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Outstanding',
      key: 'outstanding',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Awaiting Dispatch',
      key: 'awaitingDispatch',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
    {
      name: 'Reserved',
      key: 'reserved',
      editable: false,
      resizable: true,
      formatter: NumericFormatter,
      options: {
        format: { decimals: precision },
      },
    },
  ];

  const { loading, data } = useQuery<IStockTotalsQueryResult, IStockTotalQueryVariables>(stockTotalsQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    variables: { productId: productId ?? values.id },
    skip: !connectedForm.initialized || !values.id,
  });

  if (loading) {
    return null;
  }

  let stockLevelData = data?.data || [];
  stockLevelData = stockLevelData.filter((data) => {
    return !data?.warehouse?.archived;
  });

  return (
    <Form.Row>
      <Form.Fieldset cols={1}>
        <GridContainer>
          <EditableDataGrid
            columns={stockTotalsColumns}
            addBlankRow={false}
            enableCellSelect={true}
            enableCellAutoFocus={false}
            listData={stockLevelData}
            rowsCount={stockLevelData.length}
            contentHeight={window.innerHeight - 250}
            initialSortColumn="warehouse.description"
            initialSortDir="ASC"
          />
        </GridContainer>
      </Form.Fieldset>
    </Form.Row>
  );
};
